import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  Target,
  Users,
  DollarSign,
  Calendar,
  AlertTriangle,
  Lightbulb,
  Star,
  Database,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ShoppingBag,
  Clock,
  Sparkles,
  BarChart3,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { api, type PODProductType } from '../api/client';
import clsx from 'clsx';

type RealDataContext = {
  etsy?: {
    totalListings: number;
    priceRange: { min: number; max: number; avg: number; median: number };
    competitionLevel: 'low' | 'medium' | 'high' | 'very_high';
    topListingsCount: number;
    fetchedAt: string;
  };
  googleTrends?: {
    interestOverTime: number;
    trend: 'rising' | 'stable' | 'declining' | 'unknown';
    relatedQueries: string[];
    seasonalPeaks: string[];
    fetchedAt: string;
  };
};

type NicheAnalysis = {
  summary: string;
  whyTrending: string[];
  competitionLevel: string;
  competition: {
    estimatedListingCount: string;
    topSellerDominance: string;
    newSellerSuccess: string;
    entryBarriers: string[];
    differentiationPotential: string;
  };
  demand: {
    signals: Array<{ signal: string; strength: string; source: string }>;
    searchVolumeEstimate: string;
    buyerIntent: string;
    giftPotential: boolean;
  };
  seasonality: {
    isPeakSeason: boolean;
    currentSeasonScore: number;
    peakMonths: string[];
    lowMonths: string[];
    yearRoundViability: boolean;
  };
  targetAudience: Array<{
    persona: string;
    demographics: string;
    motivation: string;
  }>;
  priceRange: {
    low: number;
    high: number;
    sweet_spot: number;
    premiumJustification: string;
  };
  opportunities: string[];
  risks: string[];
  recommendedProductTypes: Array<{
    type: string;
    difficulty: string;
    profitMargin: string;
  }>;
  opportunityScore: number;
  confidenceScore: number;
  analyzedAt: string;
  realData?: RealDataContext;
};

type Niche = {
  id: number;
  keyword: string;
  trend_score: number;
  status: string;
  source?: string;
  product_type?: PODProductType;
  recipe_id?: number | null;
  analysis?: string;
  created_at: string;
};

const APPAREL_TYPES = ['t-shirt', 'hoodie', 'sweatshirt', 'tank-top'];

function isApparelType(productType: string): boolean {
  const normalized = productType.toLowerCase().replace(/\s+/g, '-');
  return APPAREL_TYPES.some(
    (type) => normalized.includes(type) || type.includes(normalized.replace('-', ' '))
  );
}

function getDifficultyColor(difficulty: string): string {
  switch (difficulty.toLowerCase()) {
    case 'easy':
      return 'bg-green-100 text-green-700';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700';
    case 'hard':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function getMarginColor(margin: string): string {
  const lower = margin.toLowerCase();
  if (lower.includes('high') || lower.includes('excellent')) return 'text-green-600';
  if (lower.includes('medium') || lower.includes('moderate')) return 'text-yellow-600';
  if (lower.includes('low')) return 'text-red-600';
  return 'text-gray-600';
}

const sourceConfig: Record<string, { label: string; bg: string; text: string; border: string }> = {
  pinterest: {
    label: 'Pinterest',
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
  tiktok: {
    label: 'TikTok',
    bg: 'bg-pink-50',
    text: 'text-pink-600',
    border: 'border-pink-200',
  },
  combined: {
    label: 'Multi-source',
    bg: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-200',
  },
  'auto-scout': {
    label: 'Auto-Scout',
    bg: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
  },
  manual: { label: 'Manual', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

export default function NicheAnalysis() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['niche', id],
    queryFn: async () => {
      const res = await api.niches.list();
      const niche = res.niches.find((n: Niche) => n.id === Number(id));
      if (!niche) throw new Error('Niche not found');
      return niche as Niche;
    },
    enabled: !!id,
  });

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get(),
  });

  const niche = data;
  const settings = settingsData?.settings;

  const getAnalysis = (n: Niche): NicheAnalysis | null => {
    if (!n.analysis) return null;
    try {
      return JSON.parse(n.analysis) as NicheAnalysis;
    } catch {
      return null;
    }
  };

  const handleReanalyze = async () => {
    if (!niche) return;
    try {
      await api.research.analyze(niche.id, settings?.default_research_model || undefined);
      refetch();
    } catch (error) {
      alert(`Analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-etsy-orange mx-auto mb-4" />
          <p className="text-gray-500">Loading niche analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !niche) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card bg-red-50 border-red-200">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Niche Not Found</h2>
          <p className="text-red-600 mb-4">
            The niche you're looking for doesn't exist or has been deleted.
          </p>
          <Link to="/research" className="btn btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Research
          </Link>
        </div>
      </div>
    );
  }

  const analysis = getAnalysis(niche);
  const score = Math.round(niche.trend_score * 100);
  const source = sourceConfig[niche.source || 'manual'] || sourceConfig.manual;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/research"
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Research
        </Link>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{niche.keyword}</h1>
              <span
                className={clsx(
                  'px-2 py-1 rounded-full text-sm font-medium',
                  source.bg,
                  source.text
                )}
              >
                {source.label}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Added {new Date(niche.created_at).toLocaleDateString()}
              </span>
              {niche.product_type && niche.product_type !== 'all' && (
                <span className="capitalize bg-gray-100 px-2 py-0.5 rounded">
                  {niche.product_type}
                </span>
              )}
            </div>
          </div>

          {/* Score Card */}
          <div
            className={clsx(
              'flex items-center gap-4 px-6 py-4 rounded-xl',
              score >= 70 ? 'bg-green-50' : score >= 50 ? 'bg-amber-50' : 'bg-gray-50'
            )}
          >
            <div className="text-center">
              <div
                className={clsx(
                  'text-4xl font-bold',
                  score >= 70 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-gray-600'
                )}
              >
                {score}%
              </div>
              <div className="text-sm text-gray-500">Trend Score</div>
            </div>
            <div className="h-12 w-px bg-gray-200" />
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-700">
                {analysis ? ((analysis.opportunityScore || 0) * 100).toFixed(0) : '--'}%
              </div>
              <div className="text-sm text-gray-500">Opportunity</div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Link
          to={`/batch?niche_id=${niche.id}`}
          className="btn btn-primary flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Generate Listings
        </Link>
        <button
          onClick={handleReanalyze}
          disabled={isRefetching}
          className="btn bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2"
        >
          {isRefetching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Re-analyze
        </button>
      </div>

      {!analysis ? (
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-4">
            <BarChart3 className="w-8 h-8 text-yellow-600" />
            <div>
              <h3 className="font-semibold text-yellow-800">No Analysis Yet</h3>
              <p className="text-yellow-700 text-sm">
                This niche hasn't been analyzed. Click "Re-analyze" to generate AI insights.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Summary</h2>
            <p className="text-gray-700 leading-relaxed">{analysis.summary}</p>
          </div>

          {/* Real Data Sources */}
          {analysis.realData && (analysis.realData.googleTrends || analysis.realData.etsy) && (
            <div className="card bg-gradient-to-r from-blue-50 to-green-50 border-blue-100">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Live Market Data
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Google Trends */}
                {analysis.realData.googleTrends && (
                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-500" />
                        <span className="font-semibold text-gray-800">Google Trends</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(analysis.realData.googleTrends.fetchedAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {/* Interest Score */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Search Interest</span>
                          <span className="text-lg font-bold text-blue-600">
                            {analysis.realData.googleTrends.interestOverTime}/100
                          </span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                            style={{ width: `${analysis.realData.googleTrends.interestOverTime}%` }}
                          />
                        </div>
                      </div>

                      {/* Trend Direction */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Trend Direction</span>
                        <span
                          className={clsx(
                            'flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium',
                            analysis.realData.googleTrends.trend === 'rising' &&
                              'bg-green-100 text-green-700',
                            analysis.realData.googleTrends.trend === 'declining' &&
                              'bg-red-100 text-red-700',
                            analysis.realData.googleTrends.trend === 'stable' &&
                              'bg-gray-100 text-gray-700'
                          )}
                        >
                          {analysis.realData.googleTrends.trend === 'rising' && (
                            <ArrowUpRight className="w-4 h-4" />
                          )}
                          {analysis.realData.googleTrends.trend === 'declining' && (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          {analysis.realData.googleTrends.trend === 'stable' && (
                            <Minus className="w-4 h-4" />
                          )}
                          {analysis.realData.googleTrends.trend.charAt(0).toUpperCase() +
                            analysis.realData.googleTrends.trend.slice(1)}
                        </span>
                      </div>

                      {/* Peak Months */}
                      {analysis.realData.googleTrends.seasonalPeaks.length > 0 && (
                        <div>
                          <span className="text-sm text-gray-600 block mb-1">Peak Months</span>
                          <div className="flex flex-wrap gap-1">
                            {analysis.realData.googleTrends.seasonalPeaks.map((month, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                              >
                                {month}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Related Queries */}
                      {analysis.realData.googleTrends.relatedQueries.length > 0 && (
                        <div>
                          <span className="text-sm text-gray-600 block mb-1">Related Searches</span>
                          <div className="flex flex-wrap gap-1">
                            {analysis.realData.googleTrends.relatedQueries
                              .slice(0, 6)
                              .map((q, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                                >
                                  {q}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Etsy Data */}
                {analysis.realData.etsy && (
                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-orange-500" />
                        <span className="font-semibold text-gray-800">Etsy Market</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(analysis.realData.etsy.fetchedAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {/* Listings Count */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Active Listings</span>
                        <span className="text-lg font-bold text-gray-800">
                          {analysis.realData.etsy.totalListings.toLocaleString()}
                        </span>
                      </div>

                      {/* Competition Level */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Competition</span>
                        <span
                          className={clsx(
                            'px-3 py-1 rounded-full text-sm font-medium capitalize',
                            analysis.realData.etsy.competitionLevel === 'low' &&
                              'bg-green-100 text-green-700',
                            analysis.realData.etsy.competitionLevel === 'medium' &&
                              'bg-yellow-100 text-yellow-700',
                            analysis.realData.etsy.competitionLevel === 'high' &&
                              'bg-orange-100 text-orange-700',
                            analysis.realData.etsy.competitionLevel === 'very_high' &&
                              'bg-red-100 text-red-700'
                          )}
                        >
                          {analysis.realData.etsy.competitionLevel.replace('_', ' ')}
                        </span>
                      </div>

                      {/* Price Stats */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <span className="text-sm text-gray-600 block mb-2">Price Range</span>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <div className="text-xs text-gray-400">Min</div>
                            <div className="font-medium">
                              ${analysis.realData.etsy.priceRange.min.toFixed(0)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">Avg</div>
                            <div className="font-medium text-green-600">
                              ${analysis.realData.etsy.priceRange.avg.toFixed(0)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">Median</div>
                            <div className="font-medium">
                              ${analysis.realData.etsy.priceRange.median.toFixed(0)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">Max</div>
                            <div className="font-medium">
                              ${analysis.realData.etsy.priceRange.max.toFixed(0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Target className="w-4 h-4" />
                Competition
              </div>
              <p
                className={clsx(
                  'text-xl font-bold capitalize',
                  analysis.competitionLevel === 'low' && 'text-green-600',
                  analysis.competitionLevel === 'medium' && 'text-yellow-600',
                  analysis.competitionLevel === 'high' && 'text-red-600'
                )}
              >
                {analysis.competitionLevel}
              </p>
            </div>
            <div className="card bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <DollarSign className="w-4 h-4" />
                Sweet Spot
              </div>
              <p className="text-xl font-bold text-green-600">${analysis.priceRange?.sweet_spot}</p>
            </div>
            <div className="card bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Lightbulb className="w-4 h-4" />
                Opportunity
              </div>
              <p className="text-xl font-bold text-purple-600">
                {((analysis.opportunityScore || 0) * 100).toFixed(0)}%
              </p>
            </div>
            <div className="card bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Calendar className="w-4 h-4" />
                Seasonality
              </div>
              <p className="text-xl font-bold text-blue-600">
                {analysis.seasonality?.yearRoundViability ? 'Year-round' : 'Seasonal'}
              </p>
            </div>
          </div>

          {/* Why Trending */}
          {analysis.whyTrending && analysis.whyTrending.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Why It's Trending
              </h2>
              <ul className="space-y-2">
                {analysis.whyTrending.map((reason, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-gray-700">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Products */}
          {analysis.recommendedProductTypes && analysis.recommendedProductTypes.length > 0 && (
            <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-purple-100">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-600" />
                AI Recommended Products
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {analysis.recommendedProductTypes.map((product, i) => {
                  const isTop = i === 0;
                  const isApparel = isApparelType(product.type);
                  return (
                    <div
                      key={i}
                      className={clsx(
                        'rounded-xl p-4 transition-all',
                        isTop
                          ? isApparel
                            ? 'bg-purple-100 border-2 border-purple-300 shadow-md'
                            : 'bg-blue-100 border-2 border-blue-300 shadow-md'
                          : 'bg-white border border-gray-200'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {isTop && <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />}
                        <span className={clsx('font-semibold', isTop && 'text-lg')}>
                          {product.type}
                        </span>
                        {isTop && (
                          <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                            Top Pick
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            getDifficultyColor(product.difficulty)
                          )}
                        >
                          {product.difficulty}
                        </span>
                        <span
                          className={clsx(
                            'text-sm font-medium',
                            getMarginColor(product.profitMargin)
                          )}
                        >
                          {product.profitMargin} margin
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Price Range */}
            {analysis.priceRange && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Price Strategy
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b">
                    <div className="flex items-center gap-8">
                      <div className="text-center">
                        <div className="text-sm text-gray-500">Low</div>
                        <div className="text-xl font-bold text-gray-600">
                          ${analysis.priceRange.low}
                        </div>
                      </div>
                      <div className="text-center bg-green-50 px-4 py-2 rounded-lg">
                        <div className="text-sm text-green-600">Sweet Spot</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${analysis.priceRange.sweet_spot}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-500">High</div>
                        <div className="text-xl font-bold text-gray-600">
                          ${analysis.priceRange.high}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Premium Justification</span>
                    <p className="text-gray-700 mt-1">{analysis.priceRange.premiumJustification}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Seasonality */}
            {analysis.seasonality && (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Seasonality
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'px-3 py-1 rounded-full text-sm font-medium',
                        analysis.seasonality.yearRoundViability
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      )}
                    >
                      {analysis.seasonality.yearRoundViability ? 'Year-Round Viable' : 'Seasonal'}
                    </span>
                    {analysis.seasonality.isPeakSeason && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700">
                        Peak Season Now
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Peak Months</span>
                      <div className="flex flex-wrap gap-1">
                        {analysis.seasonality.peakMonths?.map((month, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                          >
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block mb-1">Low Months</span>
                      <div className="flex flex-wrap gap-1">
                        {analysis.seasonality.lowMonths?.map((month, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Target Audience */}
          {analysis.targetAudience && analysis.targetAudience.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Target Audience
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {analysis.targetAudience.map((audience, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{audience.persona}</h4>
                    <p className="text-sm text-gray-600 mb-2">{audience.demographics}</p>
                    <p className="text-sm text-blue-600 italic">"{audience.motivation}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opportunities & Risks */}
          <div className="grid md:grid-cols-2 gap-6">
            {analysis.opportunities && analysis.opportunities.length > 0 && (
              <div className="card bg-green-50 border-green-100">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-green-600" />
                  Opportunities
                </h2>
                <ul className="space-y-2">
                  {analysis.opportunities.map((opp, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span className="text-gray-700">{opp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.risks && analysis.risks.length > 0 && (
              <div className="card bg-red-50 border-red-100">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Risks to Consider
                </h2>
                <ul className="space-y-2">
                  {analysis.risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">!</span>
                      <span className="text-gray-700">{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="card bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-sm text-gray-500">
                <span>
                  Analysis generated:{' '}
                  {analysis.analyzedAt ? new Date(analysis.analyzedAt).toLocaleString() : 'Unknown'}
                </span>
                <span className="mx-3">•</span>
                <span>Confidence: {((analysis.confidenceScore || 0) * 100).toFixed(0)}%</span>
              </div>
              <div className="flex gap-3">
                <Link
                  to={`/batch?niche_id=${niche.id}`}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate from this niche
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
